import { yupResolver } from '@hookform/resolvers/yup';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import * as yup from 'yup';

import { getFlexPassPartners, validateFlexPassInvoice } from '../../../util/api';
import { ORIGIN_CONTEXT } from '../../../util/checkoutContext';
import { isCordova } from '../../../util/mobile/deviceDetection';
import ControlledSelect from '../../BasicUI/ControlledInputs/ControlledSelect';
import Link from '../../Links/Link/Link';
import AnimatedLogo from '../../Logo/VanSite/AnimatedLogo';

// Minimum time the "verifying your booking" step stays visible, so the customer
// perceives the check instead of the offer flashing up instantly — which reads
// as "you were just charged 650€". Skipped in tests so assertions don't need
// to wait it out.
const MIN_LOADING_MS = process.env.NODE_ENV === 'test' ? 0 : 3000;

const MAX_PDF_BYTES = 10 * 1024 * 1024;

const StoryblokFlexPassPresetComponent = ({ blok }) => {
  const intl = useIntl();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const prefilledPartnerId = searchParams.get('id') || '';
  const successParam = searchParams.get('success');
  const sessionId = searchParams.get('session_id');

  const initialStatus = useMemo(() => {
    if (successParam === 'true') return 'payment-success';
    if (successParam === 'false') return 'payment-cancelled';
    return 'idle';
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [status, setStatus] = useState(initialStatus);
  const [errorMessage, setErrorMessage] = useState('');
  const [bookingDetails, setBookingDetails] = useState(null);
  const [partnerName, setPartnerName] = useState('');
  const [partners, setPartners] = useState([]);
  const [partnersError, setPartnersError] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [fileError, setFileError] = useState('');

  useEffect(() => {
    // Re-fires whenever status transitions back to 'idle' (e.g. "try again"
    // after a payment-cancelled redirect), not just on mount — otherwise a
    // component that first mounts in payment-success/payment-cancelled state
    // never loads partners and the retried form is permanently unusable.
    if (status !== 'idle') return undefined;
    if (partners.length > 0 && !partnersError) return undefined;
    let cancelled = false;
    getFlexPassPartners()
      .then((result) => {
        if (!cancelled) setPartners(result?.partners || []);
      })
      .catch(() => {
        if (!cancelled) setPartnersError(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const labels = useMemo(
    () => ({
      invoiceLabel: intl.formatMessage({ id: 'FlexPassPreset.form.invoice.label' }),
      invoiceButton: intl.formatMessage({ id: 'FlexPassPreset.form.invoice.button' }),
      invoiceRequired: intl.formatMessage({ id: 'FlexPassPreset.form.invoice.required' }),
      invoiceInvalid: intl.formatMessage({ id: 'FlexPassPreset.form.invoice.invalidFile' }),
      rentalServiceLabel: intl.formatMessage({
        id: 'FlexPassPreset.form.rentalService.label',
      }),
      rentalServiceRequired: intl.formatMessage({
        id: 'FlexPassPreset.form.rentalService.required',
      }),
    }),
    [intl]
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      rentalService: prefilledPartnerId,
    },
    resolver: yupResolver(
      yup.object({
        rentalService: yup.string().required(labels.rentalServiceRequired),
      })
    ),
  });

  const handleError = useCallback(
    (error) => {
      const code = error?.code || error?.error || error?.message || '';
      if (code === 'invalid-booking') {
        setErrorMessage(intl.formatMessage({ id: 'FlexPassPreset.error.invalidBooking' }));
      } else if (code === 'provider-not-found') {
        setErrorMessage(intl.formatMessage({ id: 'FlexPassPreset.error.providerNotFound' }));
      } else if (code === 'not-partner-document') {
        setErrorMessage(intl.formatMessage({ id: 'FlexPassPreset.error.notPartnerDocument' }));
      } else if (code === 'expired-booking') {
        setErrorMessage(intl.formatMessage({ id: 'FlexPassPreset.error.expiredBooking' }));
      } else if (code === 'rental-too-long') {
        setErrorMessage(intl.formatMessage({ id: 'FlexPassPreset.error.rentalTooLong' }));
      } else if (code === 'already-redeemed') {
        setErrorMessage(intl.formatMessage({ id: 'FlexPassPreset.error.alreadyRedeemed' }));
      } else if (code === 'scan-failed') {
        setErrorMessage(intl.formatMessage({ id: 'FlexPassPreset.error.scanFailed' }));
      } else {
        setErrorMessage(intl.formatMessage({ id: 'FlexPassPreset.error.generic' }));
      }
      setStatus('error');
    },
    [intl]
  );

  const scanInvoice = useCallback(
    async ({ companyId, file }) => {
      setPartnerName(
        partners.find((p) => p.companyId === companyId)?.displayName ||
          intl.formatMessage({ id: 'FlexPassPreset.fallbackProvider' })
      );
      setStatus('loading');
      const startedAt = Date.now();
      try {
        const formData = new FormData();
        formData.append('invoice', file);
        formData.append('companyId', companyId);
        formData.append('returnUrl', `${window.location.origin}${location.pathname}`);
        formData.append('originContext', isCordova() ? ORIGIN_CONTEXT.APP : ORIGIN_CONTEXT.WEB);

        const result = await validateFlexPassInvoice(formData);
        if (!result.success) {
          handleError({ code: result.error });
          return;
        }
        setBookingDetails({
          checkoutUrl: result.checkoutUrl,
          rentalStartDate: result.rentalStartDate,
          rentalEndDate: result.rentalEndDate,
          rentalLength: result.rentalLength,
          pricePerDay: result.pricePerDay,
        });
        // Hold the verification step for a beat so the offer doesn't appear
        // instantly — an instant flip reads as a completed purchase.
        const elapsed = Date.now() - startedAt;
        if (elapsed < MIN_LOADING_MS) {
          await new Promise((resolve) => setTimeout(resolve, MIN_LOADING_MS - elapsed));
        }
        setStatus('validated');
      } catch (error) {
        handleError(error);
      }
    },
    [handleError, intl, location.pathname, partners]
  );

  const handleBuyNow = useCallback(() => {
    if (bookingDetails?.checkoutUrl) {
      window.location.href = bookingDetails.checkoutUrl;
    }
  }, [bookingDetails]);

  const onSubmitForm = useCallback(
    (data) => {
      if (!invoiceFile) {
        setFileError(intl.formatMessage({ id: 'FlexPassPreset.form.invoice.required' }));
        return;
      }
      scanInvoice({ companyId: data.rentalService, file: invoiceFile });
    },
    [intl, invoiceFile, scanInvoice]
  );

  const handleFileChange = useCallback(
    (event) => {
      const file = event.target.files?.[0] || null;
      // allow re-selecting the same file
      event.target.value = '';
      if (!file) return;
      if (file.type !== 'application/pdf' || file.size > MAX_PDF_BYTES) {
        setInvoiceFile(null);
        setFileError(intl.formatMessage({ id: 'FlexPassPreset.form.invoice.invalidFile' }));
        return;
      }
      setFileError('');
      setInvoiceFile(file);
    },
    [intl]
  );

  const handleRetry = useCallback(() => {
    setStatus('idle');
    setErrorMessage('');
    setBookingDetails(null);
  }, []);

  // Loading state
  if (status === 'loading') {
    return (
      <Box alignItems="center" display="flex" flexDirection="column" py={4}>
        <AnimatedLogo />
        <Typography mt={2} variant="h6">
          {intl.formatMessage({ id: 'FlexPassPreset.loading.title' }, { partner: partnerName })}
        </Typography>
        <Typography color="text.secondary" mt={1} variant="body2">
          {intl.formatMessage({ id: 'FlexPassPreset.loading.scanning' })}
        </Typography>
      </Box>
    );
  }

  // Payment success state (returned from Stripe Checkout)
  if (status === 'payment-success') {
    return (
      <Stack alignItems="center" py={4} spacing={3}>
        <Typography fontWeight="bold" variant="h5">
          {intl.formatMessage({ id: 'FlexPassPreset.paymentSuccess.title' })}
        </Typography>
        <Alert severity="success" sx={{ maxWidth: 500 }}>
          {intl.formatMessage({ id: 'FlexPassPreset.paymentSuccess.message' })}
        </Alert>
        <Stack maxWidth={500} spacing={1.5} width="100%">
          <Typography>
            1.{' '}
            <Link to={{ name: 'SignupPage' }}>
              {intl.formatMessage({ id: 'FlexPassPreset.success.instructions.step1' })}
            </Link>
          </Typography>
          <Typography>
            2.{' '}
            <Link to={{ name: 'PassSettingsPage' }}>
              {intl.formatMessage({ id: 'FlexPassPreset.success.instructions.step2' })}
            </Link>
          </Typography>
          <Typography>
            3. {intl.formatMessage({ id: 'FlexPassPreset.success.instructions.step3' })}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  // Payment cancelled state (returned from Stripe Checkout)
  if (status === 'payment-cancelled') {
    return (
      <Stack alignItems="center" py={4} spacing={2}>
        <Alert severity="info" sx={{ width: '100%', maxWidth: 500 }}>
          {intl.formatMessage({ id: 'FlexPassPreset.paymentCancelled.message' })}
        </Alert>
        <Button onClick={handleRetry} variant="outlined">
          {intl.formatMessage({ id: 'FlexPassPreset.error.retry' })}
        </Button>
      </Stack>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <Stack alignItems="center" py={4} spacing={2}>
        <Alert severity="error" sx={{ width: '100%', maxWidth: 500 }}>
          {errorMessage}
        </Alert>
        <Button onClick={handleRetry} variant="outlined">
          {intl.formatMessage({ id: 'FlexPassPreset.error.retry' })}
        </Button>
      </Stack>
    );
  }

  // Validated state — show booking breakdown before Stripe redirect
  if (status === 'validated' && bookingDetails) {
    const totalPrice = (bookingDetails.pricePerDay * bookingDetails.rentalLength / 100).toFixed(2);
    const pricePerNight = (bookingDetails.pricePerDay / 100).toFixed(2);
    return (
      <Stack alignItems="center" py={4} spacing={3}>
        <Typography fontWeight="bold" variant="h5">
          {intl.formatMessage({ id: 'FlexPassPreset.validated.title' })}
        </Typography>
        <Typography color="text.secondary" textAlign="center" variant="body1">
          {intl.formatMessage({ id: 'FlexPassPreset.validated.subtitle' }, { partner: partnerName })}
        </Typography>
        <Box sx={{ width: '100%', maxWidth: 500, border: 1, borderColor: 'divider', borderRadius: 2, p: 3 }}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary" variant="body2">
                {intl.formatMessage({ id: 'FlexPassPreset.validated.checkIn' })}
              </Typography>
              <Typography variant="body2">
                {new Date(bookingDetails.rentalStartDate).toLocaleDateString(intl.locale, { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary" variant="body2">
                {intl.formatMessage({ id: 'FlexPassPreset.validated.checkOut' })}
              </Typography>
              <Typography variant="body2">
                {new Date(bookingDetails.rentalEndDate).toLocaleDateString(intl.locale, { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary" variant="body2">
                {intl.formatMessage({ id: 'FlexPassPreset.validated.nights' })}
              </Typography>
              <Typography variant="body2">{bookingDetails.rentalLength}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary" variant="body2">
                {intl.formatMessage({ id: 'FlexPassPreset.validated.pricePerNight' })}
              </Typography>
              <Typography variant="body2">€{pricePerNight}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography fontWeight="bold" variant="body1">
                {intl.formatMessage({ id: 'FlexPassPreset.validated.total' })}
              </Typography>
              <Typography fontWeight="bold" variant="body1">€{totalPrice}</Typography>
            </Stack>
          </Stack>
        </Box>
        <Button color="success" fullWidth onClick={handleBuyNow} size="large" sx={{ maxWidth: 500 }} variant="contained">
          {intl.formatMessage({ id: 'FlexPassPreset.validated.buyButton' })}
        </Button>
        <Button onClick={handleRetry} size="small" variant="text">
          {intl.formatMessage({ id: 'FlexPassPreset.error.retry' })}
        </Button>
      </Stack>
    );
  }

  // Idle state — show form (Case 2)
  return (
    <Stack alignItems="center" py={4} spacing={3}>
      <Typography fontWeight="bold" variant="h5">
        {intl.formatMessage({ id: 'FlexPassPreset.title' })}
      </Typography>
      <Typography color="text.secondary" textAlign="center" variant="body1">
        {intl.formatMessage({ id: 'FlexPassPreset.subtitle' })}
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmitForm)}
        sx={{ width: '100%', maxWidth: 500 }}
      >
        <Stack spacing={2}>
          <ControlledSelect
            control={control}
            error={!!errors.rentalService}
            fullWidth
            helperText={errors.rentalService?.message}
            label={labels.rentalServiceLabel}
            name="rentalService"
            required
          >
            {partners.map((partner) => (
              <MenuItem key={partner.companyId} value={partner.companyId}>
                {partner.displayName}
              </MenuItem>
            ))}
          </ControlledSelect>
          {partnersError ? (
            <Alert severity="error">
              {intl.formatMessage({ id: 'FlexPassPreset.error.partnersFailed' })}
            </Alert>
          ) : null}
          <Stack spacing={1}>
            <Typography variant="body2">{labels.invoiceLabel}</Typography>
            <Button component="label" variant="outlined">
              {invoiceFile ? invoiceFile.name : labels.invoiceButton}
              <input
                accept="application/pdf"
                hidden
                onChange={handleFileChange}
                type="file"
              />
            </Button>
            {fileError ? <Alert severity="error">{fileError}</Alert> : null}
          </Stack>
          <Button color="success" fullWidth size="large" type="submit" variant="contained">
            {intl.formatMessage({ id: 'FlexPassPreset.form.submit' })}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

StoryblokFlexPassPresetComponent.displayName = 'StoryblokFlexPassPreset';

const StoryblokFlexPassPreset = memo(StoryblokFlexPassPresetComponent);

export default StoryblokFlexPassPreset;
